// University of California, Berkeley: public college page (#2596), official
// facts only.
//
// Every figure below was read on 2026-09-30 from the source it names:
//  - UC Berkeley's Common Data Set 2025-2026, the workbook linked as
//    "2025-2026" from the Office of Planning and Analysis page (a Google
//    Sheets file; the page itself is cited because the sheet is not on a
//    berkeley.edu address). C1 gives Fall 2025 applicants, admits and
//    enrollees; its residency table is blank ("State applicant data not yet
//    available"). C8 marks the SAT and ACT "Not considered for admission,
//    even if submitted" for Fall 2027, although its C7 table still marks
//    standardized test scores "Considered". C21 and C22 answer no to early
//    decision and early action. Its G section is headed 2025-2026, so costs
//    come from the aid office's 2026-27 page instead.
//  - UC Berkeley admissions pages (dates and deadlines, first-year policies)
//    and the UC systemwide first-year dates page and application guide.
//  - Financial Aid and Scholarships Office, 2026-27 budget for new students.
//    The out-of-state total adds the Nonresident Supplemental Tuition the
//    same page states ($39,270) to the published in-state total.
//  - College Scorecard, school id 110635, read from the Scorecard school
//    page (the API's demo key was over its rate limit; same data).
// Nothing here is profile-derived, a fit score, anyone's odds, or a
// self-reported record. Re-check a figure against its source before editing
// it, and move `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'UC Berkeley Common Data Set 2025-2026',
        url: 'https://opa.berkeley.edu/campus-data/common-data-set',
        read: '2026-09-30',
    },
    dates: {
        label: 'UC Berkeley Admissions, Dates and Deadlines',
        url: 'https://admissions.berkeley.edu/apply-to-berkeley/dates-deadlines/',
        read: '2026-09-30',
    },
    ucDates: {
        label: 'UC Admissions, First-Year Dates and Deadlines',
        url: 'https://admission.universityofcalifornia.edu/how-to-apply/applying-as-a-first-year/dates-and-deadlines.html',
        read: '2026-09-30',
    },
    tests: {
        label: 'UC Berkeley Admissions, First-Year Policies',
        url: 'https://admissions.berkeley.edu/apply-to-berkeley/first-year-applicants-uc-berkeley/first-year-policy-changes/',
        read: '2026-09-30',
    },
    coa: {
        label: 'UC Berkeley Financial Aid, Student Budgets 2026-27',
        url: 'https://financialaid.berkeley.edu/how-aid-works/student-budgets-cost-of-attendance/',
        read: '2026-09-30',
    },
    ucGuide: {
        label: 'UC Admissions, Quick Reference Guide to UC Admissions (August 2026)',
        url: 'https://admission.universityofcalifornia.edu/counselors/_files/documents/quick-reference-guide-to-uc-admissions.pdf',
        read: '2026-09-30',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?110635-University-of-California-Berkeley',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'university-of-california-berkeley',
    kbId: 'university_of_california_berkeley',
    name: 'University of California, Berkeley',
    shortName: 'UC Berkeley',
    aliases: ['Cal', 'Berkeley'],
    location: 'Berkeley, CA',
    control: 'Public',
    lastChecked: '2026-09-30',
    sources: SOURCES,
    // From the KB's sourced branding (UC Berkeley brand guidelines); decoration only.
    branding: {
        primary: '#002676',
        secondary: '#FDB515',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/university_of_california_berkeley_logo.png',
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 126864,
        admitted: 14524,
        enrolled: 6687,
        source: 'cds',
        where: 'section C1',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'UC Berkeley\'s Common Data Set reports one first-year acceptance rate for the whole university, with no rate by college or major, and it leaves the in-state and out-of-state breakdown blank for Fall 2025.',
        source: 'cds',
    },
    earlyRounds: {
        title: 'Early decision and early action',
        note: 'UC Berkeley offers neither. The Common Data Set answers no to both, and the admissions office says it does not offer early admission or early decision.',
        source: 'cds',
        where: 'sections C21 and C22',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Application window', value: 'Oct 1 to Nov 30' },
        rows: [
            { plan: 'UC application (one filing period)', deadline: 'November 30', decision: 'End of March', binding: false },
        ],
        note: 'You apply on the UC application, which opens August 1. Some applicants are invited to send up to two letters of recommendation, due January 10; letters are voluntary and only invited applicants can send them.',
        source: 'dates',
        binding: 'Nothing is binding. For fall 2027 the University of California has one filing period, October 1 to November 30, with admission decisions in March.',
        bindingSource: 'ucDates',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-blind',
        blind: true,
        line: 'UC Berkeley calls itself test-free: SAT and ACT scores are not used in any part of the application process and are not considered even if you send them. If you are admitted, scores you sent are used only for course placement.',
        source: 'tests',
        cdsLine: 'The Common Data Set marks the SAT and ACT as not considered for admission, even if submitted, for fall 2027 applicants. Its separate table of admission factors still lists test scores as considered, which disagrees with that answer and with Berkeley\'s own page.',
        cdsWhere: 'sections C7 and C8',
    },

    // Changes already in effect for this cycle (#2651 review): they apply to
    // courses taken from fall 2026 on, so to this year's seniors too.
    currentChanges: [
        { topic: 'Math', line: 'A statistics or additional math course taken in fall 2026 or later no longer makes up for a missing or low grade in Algebra II or Math III. It still counts as an extra year of math. Seniors applying this fall: this already applies to courses you take this year.', source: 'ucGuide' },
        { topic: 'Math', line: 'From fall 2026, AP Statistics no longer meets two years of the UC math requirement. AP Precalculus (with a score of 4 or 5), AP Calculus AB, AP Calculus BC and IB HL Mathematics Analysis and Approaches still do.', source: 'ucGuide' },
    ],
    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-09-30',
        changes: [],
        source: 'dates',
    },

    cost: {
        year: '2026-2027',
        columns: ['In-state', 'Out-of-state'],
        rows: [
            { item: 'Tuition and fees', amounts: [18214, 18214] },
            { item: 'Nonresident supplemental tuition', amounts: [0, 39270] },
            { item: 'Housing (residence hall)', amounts: [16640, 16640] },
            { item: 'Meal plan', amounts: [7000, 7000] },
            { item: 'Other food', amounts: [2388, 2388] },
            { item: 'Student health insurance', amounts: [5066, 5066] },
            { item: 'Books, course materials, supplies and equipment', amounts: [1342, 1342] },
            { item: 'Miscellaneous and personal', amounts: [3270, 3270] },
            { item: 'Transportation', amounts: [754, 754] },
        ],
        totals: [54674, 93944],
        note: 'The budget for a new student living in a residence hall. Health insurance can be waived if you have equivalent coverage. Berkeley publishes the in-state total; the out-of-state total adds the $39,270 nonresident tuition the same page states.',
        source: 'coa',
        netPrice: {
            average: 14538,
            byIncome: [
                { income: '$0 to $30,000', amount: 5110 },
                { income: '$30,001 to $48,000', amount: 5391 },
                { income: '$48,001 to $75,000', amount: 9606 },
                { income: '$75,001 to $110,000', amount: 16064 },
                { income: 'Over $110,000', amount: 37451 },
            ],
            note: 'What first-year, full-time in-state students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from UC Berkeley\'s aid offer.',
            tileDetail: 'Per year, after grants, in-state federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
